import type { DashboardData, Person, Project, Session, TaskPayload, WorkLog, WorkLogPayload } from "./types";

type ErrorBody = { error?: string };

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = "Something went wrong.";
    try {
      const body = (await response.json()) as ErrorBody;
      if (body.error) message = body.error;
    } catch {
      // Keep the safe fallback message.
    }
    throw new Error(message);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const worklogApi = {
  session: () => api<Session>("/api/session"),

  dashboard: (month: string) =>
    api<DashboardData>(`/api/dashboard?month=${encodeURIComponent(month)}`),

  logs: (month: string) =>
    api<{ logs: WorkLog[] }>(`/api/work-logs?month=${encodeURIComponent(month)}`).then(
      (result) => result.logs,
    ),

  createLog: (payload: WorkLogPayload) =>
    api<{ id: string }>("/api/work-logs", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateLog: (id: string, payload: WorkLogPayload) =>
    api<{ ok: true }>(`/api/work-logs/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  deleteLog: (id: string) =>
    api<void>(`/api/work-logs/${encodeURIComponent(id)}`, { method: "DELETE" }),

  createBoard: (payload: { projectId: string; name: string }) =>
    api<{ id: string }>("/api/boards", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  createTask: (payload: TaskPayload) =>
    api<{ id: string }>("/api/tasks", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  moveTask: (id: string, columnId: string) =>
    api<{ ok: true }>(`/api/tasks/${encodeURIComponent(id)}/status`, {
      method: "PUT",
      body: JSON.stringify({ columnId }),
    }),

  createPerson: (payload: Pick<Person, "name" | "email" | "role"> & { hourlyRate: number }) =>
    api<{ id: string }>("/api/people", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updatePerson: (
    id: string,
    payload: Pick<Person, "name" | "role" | "active"> & { hourlyRate: number },
  ) =>
    api<{ ok: true }>(`/api/people/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  createProject: (payload: Pick<Project, "name" | "clientName">) =>
    api<{ id: string }>("/api/projects", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateProject: (id: string, payload: Pick<Project, "name" | "clientName" | "active">) =>
    api<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  updateProjectMembers: (id: string, personIds: string[]) =>
    api<{ ok: true }>(`/api/projects/${encodeURIComponent(id)}/members`, {
      method: "PUT",
      body: JSON.stringify({ personIds }),
    }),
};
